import React, { useRef } from 'react';
import { ASSET_BASE, MEDIA_V } from '../api';
import { useVocab } from '../hooks/useVocab';
import BookCredits from './BookCredits';

// DIALECT_NAMES, GRAMMAR_APPS and REGIONS used to be three tables right here, and
// the books came from a bundled import of all sixteen stories. All four now arrive
// in the manifest (scripts/library_meta.mjs -> media/library/index.json) so that
// adding a book or a language is a bake, not a deploy. See scripts/gen_library.mjs.

// 書庫 — ONE combined list across all dialects, grouped BY LANGUAGE.
//
// It used to be a flat list of cards, each carrying the language's name, icon,
// grammar link and vocab counts. That was fine while every language had exactly
// one book; the moment 會說話的葉子 landed beside 小王子 all four repeated twice
// per language, and the shelf read as if there were two 排灣語. Now the language
// says itself once, at the head of its own block: the grammar app's icon, the
// name, the counts — then its books.
//
// The icon IS the grammar link, with 文法 on it. All sixteen languages have an
// app, so the icon is never orphaned.
//
// `lib` is the manifest; `onSelect` receives a manifest ROW, not a story. The row
// carries the digest the story is fetched under, and App does the fetching —
// opening a book is now a network call, so somewhere has to own the wait.
// `opening` is the code of the book currently being fetched, if any.
export default function TextBrowser({ lib, onSelect, opening }) {
  const { vocab } = useVocab();
  const regionHeadings = useRef({});

  const counts = (dialect) => {
    let learning = 0, known = 0;
    for (const [k, c] of Object.entries(vocab)) {
      if (!k.startsWith(dialect + ':')) continue;
      if (c.status === 'learning') learning++;
      else if (c.status === 'known') known++;
    }
    return { learning, known };
  };

  // 小王子 before 會說話的葉子: the root book (book === null) leads. Sorted here
  // rather than trusted from the manifest, which happens to emit every LP before
  // every SB — an ordering no reader of gen_library.mjs promised to keep.
  const byDialect = (d) =>
    lib.stories.filter((s) => s.dialect === d)
               .sort((a, b) => (a.book ? 1 : 0) - (b.book ? 1 : 0));

  const regions = lib.regions.filter(({ dialects }) =>
    dialects.some((d) => byDialect(d).length));

  return (
    <div className="lib-scroll">
      <h2 className="lib-h">書庫</h2>
      <nav className="lib-region-nav" aria-label="書庫地區">
        {regions.map(({ name }) => (
          <button key={name} type="button" className="lib-region-button"
            onClick={() => {
              const heading = regionHeadings.current[name];
              if (!heading) return;
              heading.focus({ preventScroll: true });
              heading.scrollIntoView({ block: 'start' });
            }}>{name}</button>
        ))}
      </nav>
      {regions.map(({ name, dialects }) => {
        return (
          <div key={name}>
            <h3 className="lib-region" tabIndex={-1}
              ref={(node) => { regionHeadings.current[name] = node; }}>{name}</h3>
            {dialects.map((dialect) => {
              const texts = byDialect(dialect);
              if (!texts.length) return null;
              const icon = dialect === 'tgdaya' ? 'seediq' : dialect;
              const c = counts(dialect);
              const g = lib.grammarApps[dialect];
              return (
                <div key={dialect} className="lib-lang">
                  <div className="lib-lang-head">
                    {/* the icon IS the grammar app, and it heads the language it
                        teaches rather than trailing the books — one icon, one
                        name, one pair of counts, above everything they cover */}
                    {g ? (
                      <a className="lib-gram-link" href={g.url} target="_blank" rel="noreferrer"
                         title={`${g.name}・${g.title}（文法課程＋AI 老師）`}>
                        <img className="lib-gram-icon" src={`${ASSET_BASE}/langicons/${icon}.png${MEDIA_V}`} alt={g.title} />
                        <span className="lib-gram-tag">文法</span>
                      </a>
                    ) : null}
                    <span className="lib-lang-name">{lib.dialectNames[dialect] || dialect}</span>
                    {/* the counts are per LANGUAGE, not per book — they belong
                        to the label, not repeated on each card below it */}
                    <span className="lib-lang-counts">學習中 {c.learning}　已知 {c.known}</span>
                  </div>
                  {texts.map((t) => (
                    <button key={t.id} className="lib-card" onClick={() => onSelect(t)}
                            disabled={!!opening} aria-busy={opening === t.code}>
                      <div className="lib-body">
                        <div className="lib-title">{t.title}</div>
                        {/* 《》 because it is a book title, and the language name
                            that used to share this line now sits in the header */}
                        <div className="lib-sub">《{t.titleTranslation}》</div>
                        <div className="lib-credit">
                          <BookCredits credit={t} />
                        </div>
                        {t.reviewers && (
                          <div className="lib-credit">文字校訂｜{t.reviewers}</div>
                        )}
                        {/* sentence count is baked into the manifest — the paragraphs
                            it used to be counted from are no longer in the bundle */}
                        <div className="lib-meta">
                          {opening === t.code ? '載入中…' : `${t.sentences} 句`}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
